import { money } from '../lib/format.js';
import { Badge, Button, TextInput } from './ui.js';

/**
 * Catalogue filter sidebar.
 *
 * Filter state lives in the URL, not in component state. That single decision
 * gives back/forward navigation, shareable links, and a page that survives a
 * refresh — all of which a customer expects from a shop and none of which work
 * if the filters only exist in React state.
 *
 * The price inputs are uncontrolled and committed on blur or Enter rather than
 * on every keystroke. A controlled numeric input that refetches per character
 * fires a request for "7", "74", "749" and "7490" while the customer is typing.
 */

export interface Facets {
  price: { minMinor: number; maxMinor: number };
  total: number;
  tags: { tag: string; count: number }[];
  categories: { slug: string; name: string; count: number }[];
}

export interface FilterState {
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  inStock?: boolean;
  sort?: string;
}

interface FiltersProps {
  facets: Facets | null;
  active: FilterState;
  onChange: (next: FilterState) => void;
  resultCount: number;
}

const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'name_asc', label: 'Name: A to Z' },
  { value: 'popular', label: 'Most popular' },
];

export function Filters({ facets, active, onChange, resultCount }: FiltersProps) {
  const hasFilters =
    active.category !== undefined ||
    active.minPrice !== undefined ||
    active.maxPrice !== undefined ||
    active.inStock === true;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">Filters</h2>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={() => onChange({ sort: active.sort })}>
            Clear all
          </Button>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="sort" className="block text-xs font-medium uppercase tracking-wide text-subtle">
          Sort
        </label>
        <select
          id="sort"
          value={active.sort ?? 'newest'}
          onChange={(event) => onChange({ ...active, sort: event.target.value })}
          className="h-10 w-full rounded-lg border border-line bg-surface px-3 text-sm"
        >
          {SORTS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-xs font-medium uppercase tracking-wide text-subtle">Category</legend>
        <div className="space-y-0.5">
          <CategoryRow
            label="All categories"
            count={facets?.total ?? 0}
            selected={active.category === undefined}
            onClick={() => onChange({ ...active, category: undefined })}
          />
          {facets?.categories.map((category) => (
            <CategoryRow
              key={category.slug}
              label={category.name}
              count={category.count}
              selected={active.category === category.slug}
              onClick={() => onChange({ ...active, category: category.slug })}
            />
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-xs font-medium uppercase tracking-wide text-subtle">Price</legend>
        <PriceRange
          active={active}
          bounds={facets?.price ?? { minMinor: 0, maxMinor: 0 }}
          onChange={onChange}
        />
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="text-xs font-medium uppercase tracking-wide text-subtle">Availability</legend>
        <label className="flex cursor-pointer items-center gap-2.5 text-sm">
          <input
            type="checkbox"
            checked={active.inStock === true}
            onChange={(event) =>
              onChange({ ...active, inStock: event.target.checked ? true : undefined })
            }
            className="size-4 rounded border-line accent-[var(--accent)]"
          />
          In stock only
        </label>
      </fieldset>

      {facets && facets.tags.length > 0 && (
        <fieldset className="space-y-2">
          <legend className="text-xs font-medium uppercase tracking-wide text-subtle">
            Popular tags
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {facets.tags.slice(0, 12).map((entry) => (
              <Badge key={entry.tag} tone="neutral">
                {entry.tag}
                <span className="tabular opacity-60">{entry.count}</span>
              </Badge>
            ))}
          </div>
        </fieldset>
      )}

      <p className="tabular border-t border-line pt-4 text-xs text-subtle">
        {resultCount} product{resultCount === 1 ? '' : 's'} match
      </p>
    </div>
  );
}

function CategoryRow({
  label,
  count,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={[
        'flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left text-sm transition-colors',
        selected ? 'bg-accent-soft font-medium text-accent' : 'text-muted hover:bg-raised hover:text-ink',
      ].join(' ')}
    >
      <span className="truncate">{label}</span>
      <span className="tabular shrink-0 pl-2 text-xs opacity-70">{count}</span>
    </button>
  );
}

function PriceRange({
  active,
  bounds,
  onChange,
}: {
  active: FilterState;
  bounds: { minMinor: number; maxMinor: number };
  onChange: (next: FilterState) => void;
}) {
  /**
   * Draft values are kept local and only committed on blur or Enter. Committing
   * on every keystroke would issue a request per character typed.
   */
  const commit = (field: 'minPrice' | 'maxPrice', raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      const next = { ...active };
      delete next[field];
      onChange(next);
      return;
    }
    const major = Number(trimmed);
    if (!Number.isFinite(major) || major < 0) return;
    onChange({ ...active, [field]: Math.round(major * 100) });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-subtle">
            $
          </span>
          <TextInput
            type="number"
            inputMode="decimal"
            min={0}
            step="1"
            placeholder={String(Math.floor(bounds.minMinor / 100))}
            defaultValue={active.minPrice !== undefined ? active.minPrice / 100 : ''}
            aria-label="Minimum price"
            className="h-10 pl-6 text-sm"
            onBlur={(event) => commit('minPrice', event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commit('minPrice', event.currentTarget.value);
            }}
          />
        </div>
        <span className="text-subtle">–</span>
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-subtle">
            $
          </span>
          <TextInput
            type="number"
            inputMode="decimal"
            min={0}
            step="1"
            placeholder={String(Math.ceil(bounds.maxMinor / 100))}
            defaultValue={active.maxPrice !== undefined ? active.maxPrice / 100 : ''}
            aria-label="Maximum price"
            className="h-10 pl-6 text-sm"
            onBlur={(event) => commit('maxPrice', event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') commit('maxPrice', event.currentTarget.value);
            }}
          />
        </div>
      </div>
      <p className="tabular text-xs text-subtle">
        Catalogue range {money(bounds.minMinor)} – {money(bounds.maxMinor)}
      </p>
    </div>
  );
}
